import React, { useState } from 'react';
import { Upload, Download, CheckCircle, AlertCircle, X, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import apiService from '../../services/apiService';
import './ImportProductsModal.css';

const ImportProductsModal = ({ onClose, onImportComplete, categorias, almacenes }) => {
  const [file, setFile] = useState(null);
  const [products, setProducts] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('upload'); // 'upload', 'preview', 'importing', 'complete'
  const [importResult, setImportResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseExcelFile(selectedFile);
    }
  };

  // Función auxiliar para limpiar valores de precio (elimina $, espacios y convierte a número)
  const cleanPrice = (value) => {
    if (!value) return 0;
    const cleaned = value.toString().replace(/\$/g, '').replace(/\s/g, '').trim();
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : Math.abs(parsed); // Math.abs para manejar negativos
  };

  const parseExcelFile = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const parsedProducts = jsonData.map((row, index) => {
          const errors = [];

          // Mapeo de campos del sistema anterior al nuevo
          const nombre = row['Descripción']?.trim() || row.nombre?.trim() || '';
          const codigo_corto = row['Clave']?.toString().trim() || row.codigo_corto?.toString().trim() || '';
          const codigo_barras = row['Código de Barras']?.toString().trim() || row.codigo_barras?.toString().trim() || '';
          const categoria_nombre = row['Departamento']?.toString().trim() || row['Línea']?.toString().trim() || row.categoria?.trim() || '';

          // Precios - limpiar formato $ xxx.xx
          const precio_compra = cleanPrice(row['Último costo']) || cleanPrice(row['Costo promedio']) || cleanPrice(row.precio_compra);
          const precio1 = cleanPrice(row['Precio público']) || cleanPrice(row.precio1);
          const precio2 = cleanPrice(row['Lista de precio 2']) || cleanPrice(row.precio2);
          const precio3 = cleanPrice(row['Lista de precio 3']) || cleanPrice(row.precio3);
          const precio4 = cleanPrice(row['Lista de precio 4']) || cleanPrice(row.precio4);

          // Stock - puede venir con espacios y signo negativo
          const stockValue = row['Existencias globales'] || row['Tienda 01'] || row.stock || 0;
          const stock = Math.max(0, parseFloat(cleanPrice(stockValue))); // No permitir stock negativo

          // Unidad de medida
          let unidad_medida = (row['Unidad de salida'] || row.unidad_medida || 'pz').toString().trim().toLowerCase();
          // Convertir unidades comunes
          if (unidad_medida === 'kilo') unidad_medida = 'kg';
          if (unidad_medida === 'caja') unidad_medida = 'cja';
          if (unidad_medida === 'saco') unidad_medida = 'sco';
          if (unidad_medida === 'pieza') unidad_medida = 'pz';

          // Validaciones básicas
          if (!nombre || nombre === '') {
            errors.push('Nombre requerido');
          }

          if (!precio_compra || precio_compra <= 0) {
            errors.push('Precio de compra inválido');
          }

          // Validar que al menos un precio de venta esté configurado
          if (precio1 === 0 && precio2 === 0 && precio3 === 0 && precio4 === 0) {
            errors.push('Al menos un precio de venta requerido');
          }

          return {
            rowNumber: index + 2, // +2 porque Excel empieza en 1 y tiene header
            nombre: nombre,
            descripcion: nombre, // Usar el mismo nombre como descripción
            codigo_corto: codigo_corto,
            codigo_barras: codigo_barras,
            categoria_nombre: categoria_nombre,
            almacen_id: 1, // Almacén por defecto
            precio_compra: precio_compra,
            precio1: precio1,
            precio2: precio2,
            precio3: precio3,
            precio4: precio4,
            stock: parseInt(stock),
            stock_minimo: 5, // Stock mínimo por defecto
            unidad_medida: unidad_medida,
            errors: errors,
            valid: errors.length === 0
          };
        });

        setProducts(parsedProducts);
        setStep('preview');
      } catch (error) {
        console.error('Error al leer archivo:', error);
        setErrors(['Error al leer el archivo Excel. Verifica el formato.']);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    const validProducts = products.filter(p => p.valid);

    if (validProducts.length === 0) {
      setErrors(['No hay productos válidos para importar']);
      return;
    }

    setLoading(true);
    setStep('importing');

    try {
      // Procesar productos para enviar al backend
      const productsToImport = validProducts.map(product => {
        // Buscar ID de categoría por nombre si existe
        const categoria = categorias.find(c =>
          c.nombre.toLowerCase() === product.categoria_nombre.toLowerCase()
        );

        return {
          nombre: product.nombre,
          descripcion: product.descripcion,
          codigo_corto: product.codigo_corto,
          codigo_barras: product.codigo_barras,
          categoria_id: categoria?.id || 1, // Categoría 1 por defecto
          almacen_id: product.almacen_id || 1, // Almacén 1 por defecto
          precio_compra: product.precio_compra,
          precio1: product.precio1,
          precio2: product.precio2,
          precio3: product.precio3,
          precio4: product.precio4,
          stock: product.stock,
          stock_minimo: product.stock_minimo,
          unidad_medida: product.unidad_medida
        };
      });

      const result = await apiService.request('POST', {
        tipo: 'importarProductos',
        productos: productsToImport
      });

      if (result.success) {
        setImportResult({
          total: validProducts.length,
          exitosos: result.data.exitosos || validProducts.length,
          fallidos: result.data.fallidos || 0,
          errores: result.data.errores || []
        });
        setStep('complete');

        // Llamar callback después de 2 segundos
        setTimeout(() => {
          onImportComplete();
        }, 2000);
      } else {
        setErrors([result.message || 'Error al importar productos']);
        setStep('preview');
      }
    } catch (error) {
      console.error('Error al importar:', error);
      setErrors(['Error al importar productos']);
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    // Crear template Excel con ambos formatos como ejemplo
    const templateData = [
      {
        nombre: 'Producto Ejemplo',
        codigo_corto: 'PROD001',
        codigo_barras: '1234567890123',
        categoria: 'Abarrotes',
        precio_compra: 10.00,
        precio1: 15.00,
        precio2: 14.00,
        precio3: 13.00,
        precio4: 12.00,
        stock: 100,
        unidad_medida: 'pz'
      },
      {
        nombre: 'Galletas María',
        codigo_corto: 'GALL001',
        codigo_barras: '7501234567890',
        categoria: 'Dulces y Galletas',
        precio_compra: 8.50,
        precio1: 12.00,
        precio2: 11.50,
        precio3: 11.00,
        precio4: 10.50,
        stock: 50,
        unidad_medida: 'pq'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Productos');
    XLSX.writeFile(wb, 'plantilla_productos.xlsx');
  };

  const validProductsCount = products.filter(p => p.valid).length;
  const invalidProductsCount = products.filter(p => !p.valid).length;

  return (
    <Modal
      title="Importar Productos desde Excel"
      onClose={onClose}
      size="extra-large"
    >
      <div className="import-products-modal">
        {/* Paso 1: Subir archivo */}
        {step === 'upload' && (
          <div className="import-step">
            <div className="import-header">
              <FileSpreadsheet size={48} />
              <h3>Selecciona un archivo Excel</h3>
              <p>Sube un archivo .xlsx o .xls con los productos a importar</p>
            </div>

            <div className="import-actions">
              <Button
                variant="outline"
                onClick={downloadTemplate}
              >
                <Download size={18} />
                Descargar Plantilla
              </Button>

              <div className="file-input-container">
                <input
                  type="file"
                  id="excel-file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="file-input"
                />
                <label htmlFor="excel-file" className="file-input-label">
                  <Upload size={18} />
                  Seleccionar Archivo
                </label>
              </div>
            </div>

            <div className="import-instructions">
              <h4>Instrucciones:</h4>
              <ul>
                <li>El archivo puede ser del <strong>sistema anterior</strong> o el <strong>nuevo formato</strong></li>
                <li><strong>Sistema anterior:</strong> Clave (código corto), Descripción, Departamento, Precio público, Lista de precio 2-4, Último costo, Existencias globales, Unidad de salida</li>
                <li><strong>Nuevo formato:</strong> nombre, codigo_corto, codigo_barras, categoria, precio_compra, precio1-4, stock, unidad_medida</li>
                <li>El nombre/descripción del producto es <strong>obligatorio</strong></li>
                <li>El precio de compra debe ser <strong>mayor a 0</strong></li>
                <li>Al menos un precio de venta debe estar configurado</li>
                <li><strong>Código corto:</strong> Código interno/SKU (opcional)</li>
                <li><strong>Código de barras:</strong> Código EAN/UPC (opcional, debe ser único)</li>
                <li>El almacén se asignará automáticamente al <strong>almacén 1</strong></li>
                <li>Stock mínimo se establecerá en <strong>5 unidades</strong> por defecto</li>
              </ul>
            </div>
          </div>
        )}

        {/* Paso 2: Vista previa */}
        {step === 'preview' && (
          <div className="import-step">
            <div className="import-summary">
              <div className="summary-card valid">
                <CheckCircle size={24} />
                <div>
                  <span className="summary-count">{validProductsCount}</span>
                  <span className="summary-label">Productos Válidos</span>
                </div>
              </div>

              {invalidProductsCount > 0 && (
                <div className="summary-card invalid">
                  <AlertCircle size={24} />
                  <div>
                    <span className="summary-count">{invalidProductsCount}</span>
                    <span className="summary-label">Productos con Errores</span>
                  </div>
                </div>
              )}
            </div>

            <div className="import-preview">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Nombre</th>
                    <th>Cód. Corto</th>
                    <th>Cód. Barras</th>
                    <th>Categoría</th>
                    <th>Precio Compra</th>
                    <th>Precio 1</th>
                    <th>Stock</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, index) => (
                    <tr key={index} className={product.valid ? 'valid-row' : 'invalid-row'}>
                      <td>{product.rowNumber}</td>
                      <td>{product.nombre || '-'}</td>
                      <td>{product.codigo_corto || '-'}</td>
                      <td>{product.codigo_barras || '-'}</td>
                      <td>{product.categoria_nombre || '-'}</td>
                      <td>${product.precio_compra.toFixed(2)}</td>
                      <td>${product.precio1.toFixed(2)}</td>
                      <td>{product.stock}</td>
                      <td>
                        {product.valid ? (
                          <span className="status-valid">
                            <CheckCircle size={14} />
                            Válido
                          </span>
                        ) : (
                          <span className="status-invalid">
                            <AlertCircle size={14} />
                            {product.errors.join(', ')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="import-footer">
              <Button
                variant="outline"
                onClick={() => {
                  setStep('upload');
                  setProducts([]);
                  setFile(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleImport}
                disabled={validProductsCount === 0 || loading}
              >
                Importar {validProductsCount} Producto(s)
              </Button>
            </div>
          </div>
        )}

        {/* Paso 3: Importando */}
        {step === 'importing' && (
          <div className="import-step importing">
            <div className="loading-spinner"></div>
            <h3>Importando productos...</h3>
            <p>Por favor espera mientras procesamos los productos</p>
          </div>
        )}

        {/* Paso 4: Completado */}
        {step === 'complete' && importResult && (
          <div className="import-step complete">
            <CheckCircle size={64} className="success-icon" />
            <h3>Importación Completada</h3>

            <div className="result-summary">
              <div className="result-item">
                <span className="result-label">Total procesados:</span>
                <span className="result-value">{importResult.total}</span>
              </div>
              <div className="result-item success">
                <span className="result-label">Exitosos:</span>
                <span className="result-value">{importResult.exitosos}</span>
              </div>
              {importResult.fallidos > 0 && (
                <div className="result-item error">
                  <span className="result-label">Fallidos:</span>
                  <span className="result-value">{importResult.fallidos}</span>
                </div>
              )}
            </div>

            {importResult.errores && importResult.errores.length > 0 && (
              <div className="import-errors">
                <h4>Errores:</h4>
                <ul>
                  {importResult.errores.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <Button onClick={onClose}>
              Cerrar
            </Button>
          </div>
        )}

        {errors.length > 0 && (
          <div className="import-errors-banner">
            <AlertCircle size={20} />
            <div>
              {errors.map((error, index) => (
                <p key={index}>{error}</p>
              ))}
            </div>
            <button onClick={() => setErrors([])} className="close-errors">
              <X size={16} />
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ImportProductsModal;
